import { formatPlainDate, plural, toPlainDate } from '@/lib/format'
import type { Tone } from '@/lib/tone'
import type { RentalPayment, RentalPaymentStatus, RentalRent } from '@/pages/RealState/RentContracts/types'

export type PaymentView = 'open' | 'overdue' | 'paid' | 'canceled' | 'all'

export interface PaymentViewDef {
  value: PaymentView
  label: string
  status?: RentalPaymentStatus[]
  sort: string
}

export const PAYMENT_VIEW_PARAM = 'visao'

export const PAYMENT_VIEWS: PaymentViewDef[] = [
  { value: 'open', label: 'A receber', status: ['overdue', 'pending'], sort: 'dueDate:asc' },
  { value: 'overdue', label: 'Vencidos', status: ['overdue'], sort: 'dueDate:asc' },
  { value: 'paid', label: 'Pagos', status: ['paid'], sort: 'paidAt:desc' },
  { value: 'canceled', label: 'Cancelados', status: ['canceled'], sort: 'dueDate:desc' },
  { value: 'all', label: 'Todos', sort: 'dueDate:desc' }
]

export const paymentViewOf = (value?: string | null): PaymentViewDef => PAYMENT_VIEWS.find(view => view.value === value) ?? PAYMENT_VIEWS[0]

const DAY_MS = 86_400_000

const pad = (value: number) => String(value).padStart(2, '0')

const plainDay = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null
}

export function daysUntil(value?: string | null, today: Date = new Date()): number | null {
  if (!value) return null
  const day = plainDay(value)
  if (day === null) return null
  return Math.round((day - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / DAY_MS)
}

export const isOpenPayment = (payment: Pick<RentalPayment, 'status'>) => payment.status === 'pending' || payment.status === 'overdue'

export function effectivePaymentStatus(payment: Pick<RentalPayment, 'status' | 'dueDate'>, today: Date = new Date()): RentalPaymentStatus {
  if (payment.status !== 'pending') return payment.status
  const days = daysUntil(payment.dueDate, today)
  return days !== null && days < 0 ? 'overdue' : 'pending'
}

export function dueNote(payment: Pick<RentalPayment, 'status' | 'dueDate' | 'paidAt'>, today: Date = new Date()): string | null {
  if (payment.status === 'paid') return payment.paidAt ? `Pago em ${formatPlainDate(payment.paidAt)}` : null
  if (payment.status === 'canceled') return null
  const days = daysUntil(payment.dueDate, today)
  if (days === null) return null
  if (days === 0) return 'Vence hoje'
  if (days === 1) return 'Vence amanhã'
  return days > 0 ? `Vence em ${plural(days, 'dia', 'dias')}` : `Venceu há ${plural(-days, 'dia', 'dias')}`
}

export function dueTone(payment: Pick<RentalPayment, 'status' | 'dueDate'>, today: Date = new Date()): Tone {
  if (!isOpenPayment(payment)) return 'neutral'
  const days = daysUntil(payment.dueDate, today)
  if (days === null) return 'neutral'
  if (days < 0) return 'danger'
  return days <= 3 ? 'warning' : 'neutral'
}

export function paymentTitle(payment: Pick<RentalPayment, 'type' | 'description' | 'installment'>, installments?: number): string {
  if (payment.type === 'one_off') return payment.description || 'Cobrança avulsa'
  if (!payment.installment) return 'Aluguel'
  return installments ? `Aluguel · parcela ${pad(payment.installment)}/${pad(installments)}` : `Aluguel · parcela ${pad(payment.installment)}`
}

const MONTH_YEAR = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })

export function competenceLabel(value?: string | null): string | null {
  if (!value) return null
  const day = plainDay(value)
  if (day === null) return null
  const label = MONTH_YEAR.format(new Date(day))
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export const paymentDisplayAmount = (payment: Pick<RentalPayment, 'status' | 'amount' | 'paidAmount'>): number =>
  Number(payment.status === 'paid' && payment.paidAmount != null ? payment.paidAmount : payment.amount) || 0

export function dueSoonRange(today: Date = new Date(), days = 7): [string, string] {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + days)
  return [toPlainDate(today), toPlainDate(end)]
}

export interface LateCharges {
  daysLate: number
  fee: number
  interest: number
  total: number
}

const cents = (value: number) => Math.round(value * 100) / 100

export function lateCharges(
  payment: Pick<RentalPayment, 'status' | 'dueDate' | 'amount'>,
  rent: Pick<RentalRent, 'lateFeeType' | 'lateFeePercent' | 'lateFeeFixedAmount' | 'lateInterestPercentPerMonth'> | null | undefined,
  today: Date = new Date()
): LateCharges | null {
  if (!isOpenPayment(payment)) return null
  const days = daysUntil(payment.dueDate, today)
  if (days === null || days >= 0) return null
  const daysLate = -days
  const amount = Number(payment.amount) || 0
  const fee =
    rent?.lateFeeType === 'fixed'
      ? Number(rent.lateFeeFixedAmount) || 0
      : rent?.lateFeeType === 'percent'
        ? (amount * (Number(rent.lateFeePercent) || 0)) / 100
        : 0
  const interest = (amount * (Number(rent?.lateInterestPercentPerMonth) || 0) * daysLate) / 3000
  return { daysLate, fee: cents(fee), interest: cents(interest), total: cents(amount + fee + interest) }
}

export const inSentence = (title: string): string => (/^\p{Lu}\p{Ll}/u.test(title) ? `${title.charAt(0).toLowerCase()}${title.slice(1)}` : title)

export function paymentReminderMessage({
  tenantName,
  title,
  amount,
  dueDate,
  overdue,
  companyName,
  withBoleto = false
}: {
  tenantName?: string | null
  title: string
  amount: string
  dueDate: string
  overdue: boolean
  companyName?: string | null
  withBoleto?: boolean
}): string {
  const firstName = tenantName?.trim().split(/\s+/)[0]
  const greeting = firstName ? `Olá, ${firstName}! Tudo bem?` : 'Olá! Tudo bem?'
  const subject = `${inSentence(title)} no valor de ${amount}`
  const body = overdue
    ? `Consta em aberto o pagamento de ${subject}, que venceu em ${dueDate}. Se já pagou, pode desconsiderar e nos enviar o comprovante.`
    : `Passando para lembrar do pagamento de ${subject}, com vencimento em ${dueDate}.`
  const attachment = withBoleto ? ' Segue o boleto em anexo.' : ''
  const signature = companyName?.trim() ? `\n\n${companyName.trim()}` : ''
  return `${greeting} ${body}${attachment}${signature}`
}
